import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import './Popover.css';

/**
 * DESIGN.md §9.1 `Popover` (UI-RESEARCH.md Round 3, takeaway 1: Suno's compact "1.00x" readout
 * popover rather than a sprawling permanent widget). Anchored directly under its trigger via a
 * `position: relative` wrapper — deliberately NOT a `Portal`-rendered, `getBoundingClientRect`-
 * tracked floating element like `Dialog`/`Sheet`: this app never places a popover trigger inside
 * a scrolling or `overflow: hidden` ancestor (the transport bar is a fixed, non-scrolling strip),
 * so the simpler anchor avoids a whole class of scroll/resize-tracking bugs for zero real cost
 * here. A lighter focus contract than `Dialog` on purpose — these hold 2-4 controls, not a form:
 * focus moves in on open and returns to the trigger on close, `Esc` and an outside click both
 * close it, but (unlike `Dialog`) `Tab` is not wrapped — a real modal already exists for surfaces
 * that need that.
 */
export function Popover({
  open,
  onClose,
  trigger,
  children,
  align = 'start',
}: {
  open: boolean;
  onClose: () => void;
  trigger: ReactNode;
  children: ReactNode;
  /** Which edge of the trigger the popover's own edge lines up with. */
  align?: 'start' | 'end';
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // MVP-315: read the latest `onClose` through a ref instead of listing it as an effect dependency
  // — the same fix `useFocusTrap.ts` already carries for Dialog/Sheet, which this file never got.
  // Every caller passes an inline `onClose={() => setOpen(false)}`, a fresh function identity on
  // every render, so listing it here re-ran the effect on every render of the PARENT, not just when
  // `open` changed. In the Cockpit that parent is `OperatorConsole`, which re-renders ~4x/second
  // while a song plays (`onTimeUpdate` → `setCurrentTimeMs`), so each re-run's cleanup pushed focus
  // back to the trigger and its setup yanked focus into the panel again: a visible flicker, and a
  // native `<select>` whose dropdown was closed by the focus change before the KJ could pick a
  // device. Only `open` toggling should run the focus-capture/restore cycle.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  /**
   * MVP-684. Which way the panel opens.
   *
   * It opened upward unconditionally until this ticket, which was right for every host it had:
   * the four Cockpit controls all sat in the lower half of the screen. MVP-676 moved the volume
   * and output-device controls onto the TRANSPORT ROW, ~150px from the top of the window, and an
   * upward panel there is simply off-screen — the owner's screenshots show the first control in
   * each panel (the device Select, the "กลับไป 100%" button) sliced off by the top edge, with no
   * scroll that could reach it.
   *
   * Measured rather than passed in as a placement prop: a prop would have to be right at every
   * call site AND stay right through the next layout change, which is exactly the thing that just
   * went wrong. `useLayoutEffect` so the decision lands before paint — the panel is never seen in
   * the wrong place.
   *
   * Falls back to opening UP, which is the placement every existing host was designed around.
   */
  const [openDown, setOpenDown] = useState(false);
  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const root = rootRef.current;
    if (!panel || !root) return;
    // The gap below is `--space-2` (8px) in the stylesheet; 8 more keeps the panel clear of the
    // window edge rather than flush against it.
    const needed = panel.offsetHeight + 16;
    setOpenDown(root.getBoundingClientRect().top < needed);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusable = panel?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    // R8-P2-3: see Dialog.tsx's matching comment on the same pattern — `preventScroll` so claiming
    // focus here can never trigger a document-level scroll of its own.
    (focusable ?? panel)?.focus({ preventScroll: true });

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    }
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onCloseRef.current();
    }

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      // R8-P2-3: see Dialog.tsx's matching comment — never let restoring focus on close scroll the
      // document out from under this app's internal-only scroll containers.
      previouslyFocused.current?.focus({ preventScroll: true });
    };
    // `onClose` deliberately excluded — read via `onCloseRef` above (MVP-315).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="popover-root" ref={rootRef}>
      {trigger}
      {open && (
        <div
          className={`popover-panel popover-panel--${align}${openDown ? ' popover-panel--down' : ''}`}
          role="dialog"
          aria-modal="false"
          ref={panelRef}
          tabIndex={-1}
        >
          {children}
        </div>
      )}
    </div>
  );
}
